import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, Button, Input, Select } from '../../components/ui/Kit';
import { validateLead, hasErrors } from '../../lib/validation';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import ChannelSelector from '../../components/Leads/ChannelSelector';
import LanguagePreferenceToggle from '../../components/Leads/LanguagePreferenceToggle';
import UserPickerDropdown from '../../components/Settings/UserPickerDropdown';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  LEAD_SOURCE_FORM_OPTIONS,
  LEAD_TYPE_LABELS,
  leadTypesForChannel,
} from '../../lib/leadMeta';

const MANAGER_ROLES = new Set(['manager', 'admin', 'super_admin']);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUuid(value) {
  return typeof value === 'string' && UUID_RE.test(value);
}

function constraintFieldFromError(err) {
  if (!err || (err.code !== '23514' && err.code !== '23503')) return null;
  const details = err.details || '';
  const message = err.message || '';
  const keyMatch = details.match(/Key \(([^)]+)\)/);
  if (keyMatch) return keyMatch[1];
  const fkMatch = message.match(/constraint "[^"]*_([^_]+)_fkey"/);
  if (fkMatch) return fkMatch[1];
  const checkMatch = message.match(/constraint "([^"]+)"/);
  if (checkMatch) {
    const name = checkMatch[1];
    const col = name.replace(/^leads_/, '').replace(/_check$/, '');
    return col !== name ? col : name;
  }
  return null;
}

export default function LeadForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [ownerId, setOwnerId] = useState(null);
  const canPickOwner = MANAGER_ROLES.has(user?.role);
  const [formData, setFormData] = useState({
    channel: user?.default_channel || null,
    first_name: '',
    last_name: '',
    phone: searchParams.get('phone') || '',
    email: '',
    lead_type: '',
    lead_source: 'manual',
    notes: '',
    language: 'en',
  });

  const isRecruitFlow = location.state?.lead_type === 'recruit';

  useEffect(() => {
    if (user?.id) {
      setOwnerId((prev) => prev ?? user.id);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.default_channel && !formData.channel) {
      setFormData((prev) => ({ ...prev, channel: user.default_channel }));
    }
  }, [user?.default_channel]);

  useEffect(() => {
    if (isRecruitFlow) {
      setFormData((prev) => ({
        ...prev,
        lead_type: 'recruit',
        channel: prev.channel || user?.default_channel || 'IM',
      }));
    }
  }, [isRecruitFlow, user?.default_channel]);

  const leadTypeOptions = useMemo(() => {
    if (!formData.channel) return [];
    return leadTypesForChannel(formData.channel)
      .filter((v) => (isRecruitFlow ? v === 'recruit' : v !== 'recruit'))
      .map((value) => ({ value, label: LEAD_TYPE_LABELS[value] || value }));
  }, [formData.channel, isRecruitFlow]);

  const handleChange = (field, value) => {
    let updated = { ...formData, [field]: value };
    if (field === 'channel') {
      const allowed = leadTypesForChannel(value);
      if (updated.lead_type && !allowed.includes(updated.lead_type)) {
        updated = { ...updated, lead_type: isRecruitFlow ? 'recruit' : '' };
      }
    }
    setFormData(updated);
    if (touched[field]) setErrors(validateLead(updated, isRecruitFlow));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors(validateLead(formData, isRecruitFlow));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allTouched = Object.fromEntries(Object.keys(formData).map((k) => [k, true]));
    setTouched(allTouched);
    const newErrors = validateLead(formData, isRecruitFlow);
    if (!formData.channel) newErrors.channel = 'Channel is required';
    if (!isRecruitFlow && !formData.lead_type) newErrors.lead_type = 'Lead type is required';
    setErrors(newErrors);
    if (hasErrors(newErrors)) return;
    setLoading(true);
    try {
      const createdBy = canPickOwner ? (ownerId || user.id) : user.id;
      const payload = {
        ...formData,
        lead_type: isRecruitFlow ? 'recruit' : formData.lead_type,
        created_by: createdBy,
        agency_id: user?.agency_id,
      };
      if (isValidUuid(user?.id)) {
        payload.exclusive_to = user.id;
      }
      await api.leads.create(payload);
      addToast({
        type: 'success',
        title: `${isRecruitFlow ? 'Recruit' : 'Lead'} created`,
        message: `${formData.first_name} ${formData.last_name} was added successfully.`,
      });
      navigate(isRecruitFlow ? '/recruits' : '/leads');
    } catch (err) {
      console.error('[lead-form] api.leads.create failed', {
        code: err?.code,
        message: err?.message,
        details: err?.details,
      });
      const constraintField = constraintFieldFromError(err);
      const toastMessage = constraintField
        ? `Constraint violation on field "${constraintField}": ${err?.message || 'Please check the value and try again.'}`
        : (err?.message || 'Failed to create lead. Please try again.');
      addToast({ type: 'error', title: 'Failed to create', message: toastMessage });
      setErrors({ _form: 'Failed to create lead. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const fieldProps = (field) => ({
    value: formData[field] ?? '',
    onChange: (e) => handleChange(field, e.target.value),
    onBlur: () => handleBlur(field),
    error: touched[field] ? errors[field] : undefined,
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500"
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          Add New {isRecruitFlow ? 'Recruit' : 'Lead'}
        </h1>
      </div>

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          {errors._form && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-md border border-red-100">
              {errors._form}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Channel <span className="text-red-500">*</span>
            </label>
            <ChannelSelector
              value={formData.channel}
              onChange={(ch) => handleChange('channel', ch)}
              hideAllChip
              size="md"
            />
            {touched.channel && errors.channel && (
              <p className="mt-1 text-sm text-red-500">{errors.channel}</p>
            )}
          </div>

          {canPickOwner && (
            <div>
              <UserPickerDropdown
                label="Owner"
                source="directory"
                value={ownerId}
                onChange={setOwnerId}
                placeholder="Select owner…"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">Defaults to you if not selected</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="First Name" required {...fieldProps('first_name')} />
            <Input label="Last Name" required {...fieldProps('last_name')} />
            <Input label="Phone Number" type="tel" {...fieldProps('phone')} />
            <Input label="Email Address" type="email" {...fieldProps('email')} />
            {!isRecruitFlow ? (
              <Select
                label="Lead Type"
                options={[
                  {
                    label: formData.channel ? '— Select type —' : '— Select channel first —',
                    value: '',
                  },
                  ...leadTypeOptions,
                ]}
                value={formData.lead_type}
                onChange={(e) => handleChange('lead_type', e.target.value)}
                onBlur={() => handleBlur('lead_type')}
                error={touched.lead_type ? errors.lead_type : undefined}
                disabled={!formData.channel}
              />
            ) : (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Type</label>
                <div className="h-10 flex items-center px-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600 font-medium">
                  Recruit (Auto-selected)
                </div>
              </div>
            )}
            <Select label="Source" options={LEAD_SOURCE_FORM_OPTIONS} {...fieldProps('lead_source')} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Initial Notes</label>
            <textarea
              className="w-full rounded-md border border-gray-300 p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none h-24 resize-none"
              placeholder="Add context…"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </div>

          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 mt-1">Preferences</h3>
            <LanguagePreferenceToggle
              value={formData.language}
              onChange={(lang) => handleChange('language', lang)}
              disabled={loading}
            />
          </section>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="ghost" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner className="mr-2" />
                  Creating…
                </>
              ) : (
                `Create ${isRecruitFlow ? 'Recruit' : 'Lead'}`
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
